import React, { useState, useEffect, useCallback } from 'react';

import { Flex } from '@chakra-ui/react';
import { FormControlLabel, Checkbox, Link } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ImageUpload from 'components/ImageUpload';
import useForceUpdate from 'components/hooks/useForceUpdate';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { StyledDrawer, HeadingText } from 'styles/admin';
import {
  StyledTypography,
  ColoredCircularProgress,
  StyledButton,
  VerticalFlexContainer,
  VerticallyAlignedFlexContainer,
  NewStyledIconContainer,
  NewStyledIconButton,
  StyledBox,
} from 'styles/common';

import { sampleRosterFileURI } from 'constants/ProjectConstants';
import { StyledIcon } from 'constants/icons';
import theme from 'constants/theme';

import { uploadEmployeesCSV } from 'services/admin';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography13 } from 'uiToolkit/Typography';
import { Button } from 'uiToolkitV1/Button';

import { BulkUploadStatus } from './BulkUploadSidePanel/components/BulkUploadStatus';
import { StyledDivider } from './styles';

const BulkUploadPanel = ({ open, onCancel, onSuccess }) => {
  const dispatch = useDispatch();
  const [sendEmail, setSendEmail] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [reload, forceReload] = useForceUpdate();

  const handleKeyDown = ({ keyCode }) => {
    if (keyCode === 27) onCancel();
  };

  const validateUploadFile = (file) => {
    if (file.size > 10 * 1000 * 1000) {
      const error = 'file size cannot be more than 10 mb.';
      dispatch(showErrorSnackbar(error));
      return false;
    }
    return true;
  };

  const selectUploadFile = (file) => {
    if (validateUploadFile(file)) setSelectedFile(file);
  };

  const uploadFile = () => {
    const file = selectedFile;
    // setUploading(true);

    uploadEmployeesCSV(file, sendEmail)
      .then((_) => {
        // dispatch(
        //   showSuccessSnackbar('File upload successful. Please refresh to see updated data.', 3000)
        // );
        onSuccess();
        forceReload();
      })
      .catch((err) => {
        console.log('upload error', err.message);
        dispatch(showErrorSnackbar(err.message, 5000));
      });
    // .finally(() => forceReload());
  };

  const resetState = useCallback(() => {
    setSendEmail(true);
    setSelectedFile(null);
    setUploading(false);
  });

  useEffect(() => {
    // on close of side Panel, clear the state.
    if (!open) {
      resetState();
    }
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      ModalProps={{ onBackdropClick: onCancel }}
      onKeyDown={handleKeyDown}
      onClose={onCancel}
    >
      <Container>
        <VerticallyAlignedFlexContainer style={{ marginBottom: '12px' }}>
          <HeadingText>Bulk Upload</HeadingText>
          <NewStyledIconButton padding="large" onClick={onCancel} style={{ marginLeft: 'auto' }}>
            <StyledIcon color="textSecondary" size="header">
              <CloseIcon />
            </StyledIcon>
          </NewStyledIconButton>
        </VerticallyAlignedFlexContainer>
        <StyledDivider />

        <VerticalFlexContainer>
          <div>
            <StyledTypography weight="bold" variant="body1">
              Import names, email addresses and custom properties (department, location, role) from a CSV file.
            </StyledTypography>

            <VerticallyAlignedFlexContainer topGutter={3} bottomGutter={2}>
              <ImageUpload
                setFile={selectUploadFile}
                accept=".csv"
                icon={
                  <ButtonColorWrapper>
                    <Button isDisabled={uploading} variant="outlined" StartIcon={AddIcon}>
                      Choose File
                    </Button>
                  </ButtonColorWrapper>
                }
                disabled={uploading}
              />
              <StyledTypography color="textSecondary">
                {selectedFile === null ? 'No File Chosen' : selectedFile.name}
              </StyledTypography>
            </VerticallyAlignedFlexContainer>

            {!uploading && (
              <VerticallyAlignedFlexContainer>
                <NewStyledIconContainer color={theme.palette.icons.grey2} size="header">
                  <StyledInfoIcon />
                </NewStyledIconContainer>
                <StyledTypography variant="body1" color="textSecondary">
                  {`Please `}
                  <Link download="MeshCSVSample.csv" href={sampleRosterFileURI} color="primary">
                    download
                  </Link>
                  {` our sample file to refer to the upload format.`}
                </StyledTypography>
              </VerticallyAlignedFlexContainer>
            )}
          </div>

          <BulkUploadStatus setUploading={setUploading} reload={reload} />

          <Flex mt="auto" flexDir="column">
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendEmail}
                  onChange={({ target }) => {
                    setSendEmail(target.checked);
                  }}
                  name="sendEmail"
                  color="primary"
                />
              }
              label="Send invite email"
            />
            <Box marginTop="auto" flexcontainer marginLeft="auto" alignItems="center" justifyContent="flex-end">
              <Typography13 onClick={onCancel} style={{ width: '8rem', marginRight: 'auto' }} weight={600}>
                Cancel
              </Typography13>
              <StyledButton
                variant="contained"
                color="primary"
                size="large"
                onClick={uploadFile}
                disabled={selectedFile === null}
              >
                {uploading ? (
                  <ColoredCircularProgress size="tiny" />
                ) : (
                  <StyledTypography color="white">Upload</StyledTypography>
                )}
              </StyledButton>
            </Box>
          </Flex>
        </VerticalFlexContainer>
      </Container>
    </Drawer>
  );
};

const StyledInfoIcon = styled(InfoOutlinedIcon)`
  margin-bottom: -3px;
  margin-right: 2px;
`;

const Drawer = styled(StyledDrawer)`
  ${(props) => props.theme.breakpoints.up('sm')} {
    .MuiDrawer-paper {
      width: 40%;
      padding: 0;
    }
  }
`;

const Box = styled(StyledBox)`
  background: white;
`;

const Container = styled('div')`
  padding: 20px;
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
`;
const ButtonColorWrapper = styled('div')`
  color: ${theme.palette.text.blue};
  margin-right: 12px;
  p {
    color: ${theme.palette.text.blue} !important;
    font-weight: 400;
  }
  svg {
    color: ${theme.palette.text.blue} !important;
  }
  button {
    border-color: ${theme.palette.text.blue} !important;
  }
`;
export default BulkUploadPanel;
